import { RadioGroup, TextButton as DSTextButton } from '@wix/design-system'
import { useContent, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { usePageLogger } from '../../../bi/mfi'
import { useNext } from '../../../hooks/useNext'
import { usePrevious } from '../../../utils/usePrevious'
import { Button as HarmonyButton } from '../../reusable/Button'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import ArrowRightHarmony from '../../reusable/icons/ArrowRightHarmony.svg'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { RichText } from '../../reusable/RichText'
import { SkipButton } from '../../reusable/SkipButton'
import { TWelcomeV2 as Content } from './WelcomeV2.content'
import { welcomeV2DataHooks as dataHooks } from './WelcomeV2.dataHooks'
import s from './WelcomeV2.scss'

type ElementType<T> = T extends (infer U)[] ? U : never
type Option = ElementType<Content['question']['options']>

export const WelcomeV2: FC = () => (
  <DesignSystemTokensLoader type="odeditor">
    <WelcomeV2Template />
  </DesignSystemTokensLoader>
)

const WelcomeV2Template: FC = () => {
  const pageLogger = usePageLogger()
  const next = useNext()
  const { content } = useContent<Content>()
  const { skip, skipPosition, next: nextBtn, question, legalDisclaimer } = content
  const { questionKey, content: questionContent, options: questionOptions } = question
  const [searchParams] = useSearchParams()
  const flowContext = searchParams.get('flowContext')
  const prevFlowContext = usePrevious(flowContext)
  const [pageLoading] = usePageLoading()
  const precheckedOption = useMemo(() => {
    return content.question.options.find(option => option.isChecked)
  }, [content])

  const [clickedButton, setClickedButton] = useState<'next' | 'skip'>()

  const [selectedOption, setSelectedOption] = useState<Option | undefined>(precheckedOption)

  const handleChange = (value?: number | string) => {
    const option = questionOptions.find(option => option.optionKey === value)
    if (!option) {
      return
    }
    void pageLogger.clickAnswer({ questionKey, buttonName: String(value) })
    setSelectedOption(option)
  }

  const handleSkip = () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('skip')
    void pageLogger.generalAction({ action: 'skip' })
    next({
      [questionKey]: {
        skipped: true,
      },
    })
  }

  const handleNext = async () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('next')
    void pageLogger.answer({ answer: selectedOption?.optionKey || '' })
    next({
      [questionKey]: { values: [selectedOption?.optionKey ?? ''] },
    })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    if (prevFlowContext !== flowContext && !pageLoading) {
      setClickedButton(undefined)
      setSelectedOption(precheckedOption)
    }
  }, [clickedButton, flowContext, prevFlowContext, pageLoading, precheckedOption])

  return (
    <div className={s.container}>
      <WixLogo className={s.logo} />
      <div className={s.cardGroup}>
        <div className={s.leftCard}>
          <h2 className={s.subtitle} data-hook={dataHooks.subtitle()}>
            {questionContent.subtitle}
          </h2>
          <h1 className={s.title} data-hook={dataHooks.title()}>
            {questionContent.title}
          </h1>
          <RadioGroup
            value={selectedOption?.optionKey}
            onChange={handleChange}
            selectionArea="always"
            selectionAreaSkin="outlined"
            selectionAreaPadding="20px 24px"
            spacing="12px"
            className={s.radioGroup}
          >
            {questionOptions.map(option => (
              <RadioGroup.Radio
                key={option.optionKey}
                value={option.optionKey}
                dataHook={dataHooks.radio({ optionKey: option.optionKey })}
              >
                <span className={s.radioLabel}>{option.title}</span>
              </RadioGroup.Radio>
            ))}
          </RadioGroup>
          <HarmonyButton
            isLoading={clickedButton === 'next'}
            onClick={handleNext}
            disabled={selectedOption === undefined}
            className={s.button}
            size="large"
            skin="standard"
            dataHook={dataHooks.next()}
            label={nextBtn}
          />
          {legalDisclaimer && (
            <RichText light secondary size="small" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
              {legalDisclaimer}
            </RichText>
          )}
        </div>
        <div className={s.videoContainer}>
          <video className={s.video} loop autoPlay muted playsInline>
            {questionContent.safariSrc && (
              <source src={questionContent.safariSrc} type='video/quicktime; codecs="hvc1"' />
            )}
            <source src={questionContent.src} />
          </video>
        </div>
      </div>
      {skipPosition === 'topRight' ? (
        <SkipButton skin="harmony" skipButton={skip} onClick={handleSkip} dataHook={dataHooks.skip()} />
      ) : (
        <DSTextButton
          onClick={handleSkip}
          className={s.skipButton}
          suffixIcon={<ArrowRightHarmony />}
          dataHook={dataHooks.skip()}
          skin="dark"
          underline="none"
          size="medium"
        >
          {skip}
        </DSTextButton>
      )}
    </div>
  )
}
